/**
 * 콘솔 목록 화면의 «층» — 화면 머리 → 툴바 → 본문 (2026-09-09 사용자 지시).
 *
 * 검색·필터는 목록 **위**, 등록 단추는 툴바 **오른쪽**. 분류(`side`)를 주면 좌 240 /
 * 우 목록으로 갈리고(형 B), 안 주면 한 판이다(형 A). 규격은 `console-list.css` 참조.
 *
 * ⚠ 이 부품은 «자리»만 잡는다 — 무엇을 거를지, 무엇을 보여 줄지는 화면이 정한다.
 */
import type { ReactNode } from 'react';
import { Search, Plus } from '../../icons';
import { ConsoleScreenHead } from './ConsoleScreenHead';
import './console-list.css';

export interface ConsoleListFilter {
  key: string;
  label: string;
  /** 「누르면 몇 개 보이나」 — 정본 P3. 전체 기준 숫자를 적으면 눌렀을 때 거짓말이 된다. */
  count: number;
}

export interface ConsoleListSideItem {
  key: string;
  label: string;
  count: number;
  icon?: ReactNode;
}

export interface ConsoleListScreenProps {
  /**
   * ⚠ 경로(breadcrumb)는 **받지 않는다** — 셸(`ConsolePage`)이 이미 그린다.
   *   여기서 또 그리면 같은 경로가 두 줄로 겹친다(#1093 에서 바로잡음).
   */
  title: string;
  /** 제목 옆 요약 칩 (예: '17개 · 노출 14'). */
  kpi?: string;
  desc?: string;

  search?: { value: string; onChange: (v: string) => void; placeholder: string };
  filters?: { items: ConsoleListFilter[]; active: string; onChange: (key: string) => void };
  /** 등록 단추 — 라벨을 주면 툴바 오른쪽에 선다. */
  add?: { label: string; onClick: () => void; disabled?: boolean };
  /** 툴바 오른쪽, 등록 단추 앞에 붙일 것(연도 고르기 등). */
  toolsExtra?: ReactNode;

  /** 좌 분류(거르개). 주지 않으면 형 A — 본문이 한 판이다. */
  side?: { head: string; items: ConsoleListSideItem[]; active: string; onChange: (key: string) => void };

  /** 머리·툴바 아래에 띄우는 알림(오류 등). */
  notice?: ReactNode;
  children: ReactNode;
}

export function ConsoleListScreen({
  title, kpi, desc, search, filters, add, toolsExtra, side, notice, children,
}: ConsoleListScreenProps) {
  return (
    <section className="cl">
      <ConsoleScreenHead title={title} kpi={kpi} desc={desc} />

      <div className="cl__tools">
        {search && (
          <span className="cl-search">
            <Search size={15} />
            <input
              type="search"
              value={search.value}
              placeholder={search.placeholder}
              aria-label={search.placeholder}
              onChange={(e) => search.onChange(e.target.value)}
            />
          </span>
        )}
        {filters?.items.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`cl-chip${filters.active === f.key ? ' is-on' : ''}`}
            aria-pressed={filters.active === f.key}
            onClick={() => filters.onChange(f.key)}
          >
            {f.label} <span className="cl-chip__n">{f.count}</span>
          </button>
        ))}
        <span className="cl__spacer" />
        {toolsExtra}
        {add && (
          <button type="button" className="cl-add" disabled={add.disabled} onClick={add.onClick}>
            <Plus size={15} /> {add.label}
          </button>
        )}
      </div>

      {notice}

      <div className="cl__body">
        {side && (
          <nav className="cl-side" aria-label={side.head}>
            <div className="cl-side__head">{side.head}</div>
            {side.items.map((it) => (
              <button
                key={it.key}
                type="button"
                className={`cl-side__item${side.active === it.key ? ' is-on' : ''}`}
                aria-pressed={side.active === it.key}
                onClick={() => side.onChange(it.key)}
              >
                {it.icon}
                <span className="cl-side__label">{it.label}</span>
                <span className="cl-side__n">{it.count}</span>
              </button>
            ))}
          </nav>
        )}
        <div className="cl-main">{children}</div>
      </div>
    </section>
  );
}
